import { GeneralPropsType } from "@/common/types";
import styles from "./index.module.css";
import clsx from "clsx";
import UnderlineText from "../UnderlineText";
import { CircularProgress, cn, Modal, ModalContent, useDisclosure } from "@nextui-org/react";
import ModalLayout from "../ModalLayout";
import { useGlobalData } from "@/contexts/GlobalData";
import { MouseEventHandler, useEffect, useMemo, useState } from "react";
import WarningGroup from "../WarningGroup";
import BottomGradientBorder from "../BottomGradientBorder";
import DynamicFontSize from "../DynamicFontSize";
import PrimaryButton from "../PrimaryButton";
import getSubPaymentFeeService from "@/services/getSubPaymentFeeService";
import FormatterNumber from "../FormatterNumber";
interface SubPaymentFeeInfoPropsType extends GeneralPropsType {
  onContinue?: (data?: any) => any;
}
export interface Fee {
  time: number;
  feePercent: number;
  feeFix: number;
  isCurrent: boolean;
  isLatest: boolean;
}
const prefix = "sub_fee_";
function SubPaymentFeeInfo({
  children,
  className,
  onContinue
}: SubPaymentFeeInfoPropsType) {
  const {
    translate,
    subPaymentFeeData
  } = useGlobalData();
  const {
    isOpen: isOpenFeeInfo,
    onOpen: onOpenFeeInfo,
    onOpenChange: onOpenChangeFeeInfo,
    onClose: onCloseFeeInfo
  } = useDisclosure();
  const handleShowFee: MouseEventHandler = e => {
    e.stopPropagation();
    onOpenFeeInfo();
  };
  const handleContinue = () => {
    onCloseFeeInfo();
    translate(prefix + "can continue") == "true" && onContinue?.();
  };
  const freeFeesTime = useMemo(() => {
    return subPaymentFeeData?.filter((item: Fee) => !item?.feePercent && !item?.feeFix);
  }, [JSON.stringify(subPaymentFeeData)]);
  const currentIsFree = useMemo(() => {
    return freeFeesTime?.find((item: Fee) => item?.isCurrent);
  }, [freeFeesTime]);
  const availableFreeFee = useMemo(() => {
    const index = freeFeesTime?.findIndex((item: Fee) => item?.isCurrent);
    if (index === -1) return [];
    return freeFeesTime?.slice(index);
  }, [freeFeesTime]);
  const currentFee = useMemo(() => {
    return subPaymentFeeData?.find((item: Fee) => item?.isCurrent);
  }, [JSON.stringify(subPaymentFeeData)]);
  const currentFeeIndex = useMemo(() => {
    return subPaymentFeeData?.findIndex((item: Fee) => item?.isCurrent);
  }, [JSON.stringify(subPaymentFeeData)]);
  return <>
            {!currentFee && <CircularProgress strokeWidth={4} classNames={{
      svg: "!size-5 !text-error"
    }} />}

            {!!subPaymentFeeData?.length && <div className={clsx("", styles.container, className)} onClick={e => e.stopPropagation()}>
                    {!!currentFee && <UnderlineText onClick={handleShowFee} className={clsx("!font-medium", {
        "!text-error": !availableFreeFee?.length,
        "!text-white": !!availableFreeFee?.length
      })}>
                            {!availableFreeFee?.length && <DynamicFontSize fontSize={14}>
                                    {currentFee?.feePercent}%
                                    {!!currentFee?.feeFix && <span>
                                            + <FormatterNumber value={currentFee?.feeFix} start="$" />
                                        </span>}{" "}
                                    {translate(prefix + "fees")}
                                </DynamicFontSize>}
                            {!!availableFreeFee?.length && <DynamicFontSize fontSize={14}>
                                    {translate(prefix + "free")} ({availableFreeFee?.length}{" "}
                                    {translate(prefix + (availableFreeFee?.length > 1 ? "turns" : "turn"))})
                                </DynamicFontSize>}
                        </UnderlineText>}
                    <Modal isOpen={isOpenFeeInfo} placement="bottom" onOpenChange={onOpenChangeFeeInfo} isDismissable={false} hideCloseButton backdrop="blur">
                        <ModalContent className="rounded-none bg-transparent !mx-0 !my-0 !max-w-[100vw]">
                            <ModalLayout className="" boxClassName="max-h-dvh flex flex-col" modalKey="4295e77a-0fac-47d3-937c-97c2b00fd2596ad" title={translate(prefix + "High Transaction Fees")} onClose={onCloseFeeInfo}>
                                <div className="flex flex-col gap-4">
                                    <WarningGroup>
                                        {translate(prefix + "Using Sub-payment may incur high transaction fees, please consider before using")}
                                    </WarningGroup>
                                    <BottomGradientBorder background="top_to_bottom" className="p-3 rounded-2xl flex flex-col gap-2">
                                        <DynamicFontSize fontSize={13} className="text-white font-medium leading-[140%]">
                                            {translate(prefix + "Transaction fee regulations")}
                                        </DynamicFontSize>
                                        <div className="divider "></div>
                                        {freeFeesTime && <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <DynamicFontSize fontSize={12} className="text-description leading-[140%]">
                                                        {translate(prefix + `fee regulations free`)}
                                                    </DynamicFontSize>
                                                    {currentIsFree && <DynamicFontSize fontSize={11} className={cn("rounded-md bonus_bg leading-[100%] w-fit px-1 py-0.5 mt-1")}>
                                                            <span className={"bonus_text"}>
                                                                {translate(prefix + "current")}
                                                            </span>
                                                        </DynamicFontSize>}
                                                </div>
                                                <DynamicFontSize fontSize={13} className={clsx("font-medium leading-[140%] ml-auto", {
                    "text-white": true,
                    "text-description": false
                  })}>
                                                    {0}%
                                                </DynamicFontSize>
                                            </div>}
                                        {subPaymentFeeData?.map((item: Fee, index: number) => {
                  if (!item?.feePercent && !item?.feeFix) return null;
                  return <div className="flex items-center gap-2" key={index}>
                                                    <div className="flex items-center gap-2">
                                                        <DynamicFontSize fontSize={12} className="text-description leading-[140%]">
                                                            {translate(prefix + `fee regulations ${index}`)}
                                                        </DynamicFontSize>
                                                        {item?.isCurrent && <DynamicFontSize fontSize={11} className={cn("rounded-md bonus_bg leading-[100%] w-fit px-1 py-0.5 mt-1")}>
                                                                <span className={"bonus_text"}>
                                                                    {translate(prefix + "current")}
                                                                </span>
                                                            </DynamicFontSize>}
                                                    </div>
                                                    <DynamicFontSize fontSize={13 + (index - availableFreeFee?.length) * 13 * 0.15} className={clsx("font-medium leading-[140%] ml-auto", {
                      "text-white": index >= currentFeeIndex,
                      "text-description": index < currentFeeIndex
                    })}>
                                                        {item?.feePercent}%
                                                        {!!item?.feeFix && <span>
                                                                + <FormatterNumber value={item?.feeFix} start="$" />
                                                            </span>}{" "}
                                                    </DynamicFontSize>
                                                </div>;
                })}
                                    </BottomGradientBorder>
                                    <PrimaryButton onClick={handleContinue} className="w-full">
                                        {translate(prefix + "continue using")}
                                    </PrimaryButton>
                                </div>
                            </ModalLayout>
                        </ModalContent>
                    </Modal>
                </div>}
        </>;
}
export default SubPaymentFeeInfo;